import React, { useState, useEffect, useRef } from "react";
import { differenceInDays } from "date-fns";
import { CSVLink } from "react-csv";
import { connect } from "react-redux";
import { useParams } from "react-router";

import {
  emptyString,
  loginConstants,
  GET_RESPONSE_TYPE,
  storesExportHeaders,
  groupsExportHeaders,
  buttonGroupConstant,
  brandPerformanceStoresName,
  brandPerformanceGroupsName,
  BRAND_PERFORMANCE_SORTING,
  columnHeadersConstants,
  performanceScreensConstants,
  AVERAGE_RATING_RESPONSE_TYPE,
} from "../../constants";
import { starRatingGuestSentimentResponse } from "../../../admin-screens/constants";
import { getItemFromStorage } from "../../../../system";
import {
  fileName,
  isInclude,
  calculateChange,
  convertToDateToUnix,
  updatedColumnHeaders,
  convertFromDateToUnix,
  datePreferenceUtility,
  useRatingCheckboxGroup,
  getConfirmationModal,
  UTCToDateFormat,
  timestampToDateTimeFormat,
  handleAsyncAction,
} from "../../../../shared";
import {
  brandPerformanceActions,
  individualStoreProfileActions,
} from "../../redux/actions";

import { buildBrandPerformanceRequestBody } from "./utility";
import { useWindowWidth } from "../../../../shared/components/hooks/useWindowWidth/useWindowWidth";

import Performance from "./performance";
import FilterComponent from "./filterComponent";
import PerformanceButtonGroup from "./performanceButtonGroup";
import useModalDisplay from "../../../../shared/components/hooks/useModalDisplay/useModalDisplay";

const { AVERAGE_SENTIMENT_RATING, AVERAGE_TRANSACTION_RATING } =
  columnHeadersConstants;
const { GUEST_SENTIMENTS, GUEST_TRANSACTION } = performanceScreensConstants;

const BrandPerformance = (props) => {
  const {
    brandPerformanceReducer: {
      allItemsForExport,
      allFranchiseResponsesData,
      allRestaurantResponsesData,
      franchiseResponsesData,
      restaurantResponsesData,
      groupResponseDataPagination,
      restaurantResponseDataPagination,
      storesAvgSentimentAndTransactionRatingData,
    },
    frRoute,
    reduxBrandId,
    resetAllExportItems,
    resetRestaurantProfile,

    getAverageRatingResponses,
    getRecordPaginationResponses,
    exportDetailCustomerSurveyData,
    getAllFranchiseResponseItemsForExport,
    getAllRestaurantResponsesItemsForExport,
  } = props;

  let { brandId } = useParams();

  const csvLinkRef = useRef();
  const [exportData, setExportData] = useState([]);
  const [exportHeaders, setExportHeaders] = useState([]);
  const [sorting, setSorting] = useState(BRAND_PERFORMANCE_SORTING);
  const windowSize = useWindowWidth();
  const [selectedOption, setSelectedOption] = useState([]);
  const [selectedResponse, setSelectedResponse] = useState(
    buttonGroupConstant.STORE,
  );
  const [dateState, setDateState] = useState(datePreferenceUtility());
  const dayCounts = differenceInDays(dateState.endDate, dateState.startDate) + 1;
  const [exportFileName, setExportFileName] = useState(emptyString);
  const [searchString, setSearchString] = useState(emptyString);

  const [showModal, modalElement, setComponent, closeModal] = useModalDisplay();

  const createDateFrom = convertFromDateToUnix(dateState.startDate);
  const createDateTo = convertToDateToUnix(dateState.endDate);

  const { handleRatingOptionChange, selectedRatingOption } =
    useRatingCheckboxGroup(selectedOption, setSelectedOption);

  //user role and id's from the session
  const userRole = getItemFromStorage(loginConstants.ROLE);

  //Handle change in response and reset states to its original values.
  const handleChangeResponse = (value) => {
    setSelectedOption([]);
    setSearchString(emptyString);
    setSelectedResponse(value);
  };

  // Build requests without changing the selected sort or filters.
  const getPaginatedPostRequestBody = (
    pageNumber,
    sortBy,
    size,
    pagination = restaurantResponseDataPagination,
  ) =>
    buildBrandPerformanceRequestBody({
      pagination,
      pageNumber,
      sortBy,
      size,
      sorting,
      selectedOption,
      createDateFrom,
      createDateTo,
      searchString,
      selectedRatingOption,
      frRoute,
    });

  // get table data with pagination
  const getData = (pageNumber, sortBy, size) => {
    if (sortBy) setSorting(sortBy);
    const requestBody = getPaginatedPostRequestBody(pageNumber, sortBy, size);

    brandId =
      reduxBrandId === emptyString
        ? getItemFromStorage(loginConstants.BRAND_ID)
        : reduxBrandId;

    const isSentimentSelected =
      isInclude(requestBody.filter, GUEST_SENTIMENTS) &&
      selectedRatingOption === GUEST_SENTIMENTS;
    const isTransactionSelected =
      isInclude(requestBody.filter, GUEST_TRANSACTION) &&
      selectedRatingOption === GUEST_TRANSACTION;
    const isNeitherSelected = !(
      isInclude(requestBody.filter, GUEST_TRANSACTION) ||
      isInclude(requestBody.filter, GUEST_SENTIMENTS)
    );

    if (isSentimentSelected || isTransactionSelected || isNeitherSelected) {
      const {
        GET_STORE_AVERAGE_RATING_RESPONSES,
        GET_GROUPS_AVERAGE_RATING_RESPONSES,
      } = AVERAGE_RATING_RESPONSE_TYPE;

      if (selectedResponse === buttonGroupConstant.STORE) {
        const { GET_RESTAURANT_RESPONSES } = GET_RESPONSE_TYPE;

        const payload = {
          createDateFrom: createDateFrom,
          createDateTo: createDateTo,
          id: brandId,
          type: GET_RESTAURANT_RESPONSES,
          ...requestBody,
        };
        getRecordPaginationResponses(payload);
        getAverageRatingResponses({
          createDateFrom: createDateFrom,
          createDateTo: createDateTo,
          id: brandId,
          type:
            selectedResponse === buttonGroupConstant.STORE
              ? GET_STORE_AVERAGE_RATING_RESPONSES
              : GET_GROUPS_AVERAGE_RATING_RESPONSES,
          ...requestBody,
        });
      } else {
        const { GET_GROUP_RESPONSES } = GET_RESPONSE_TYPE;
        const { GET_GROUP_AVERAGE_RATING_RESPONSES } =
          AVERAGE_RATING_RESPONSE_TYPE;

        const basePayload = {
          createDateFrom,
          createDateTo,
          id: brandId,
          ...requestBody,
        };

        // For record pagination
        getRecordPaginationResponses({
          ...basePayload,
          type: GET_GROUP_RESPONSES,
        });

        // For average rating responses
        getAverageRatingResponses({
          ...basePayload,
          type: GET_GROUP_AVERAGE_RATING_RESPONSES,
        });
      }
    }
  };

  //Used to export CSV
  const exportCSV = () => {
    if (csvLinkRef.current) {
      csvLinkRef.current.link.click();
    }
  };

  //Get all data for export
  const getExportData = (type) => {
    let headers;
    let requestId;
    //Determine requestId based on the type
    if (type === buttonGroupConstant.STORE) {
      headers = storesExportHeaders;
      requestId = brandId || reduxBrandId;
      setExportFileName(brandPerformanceStoresName);
    } else {
      headers = groupsExportHeaders;
      requestId = brandId || reduxBrandId;
      setExportFileName(brandPerformanceGroupsName);
    }

    //Create the requestBody object
    const pagination = type === buttonGroupConstant.STORE
      ? restaurantResponseDataPagination
      : groupResponseDataPagination;
    const requestBody = getPaginatedPostRequestBody(
      pagination.number,
      null,
      selectedResponse === buttonGroupConstant.STORE
        ? restaurantResponseDataPagination.size
        : groupResponseDataPagination.size,
      pagination,
    );

    //Determine the appropriate export function based on type
    if (type === buttonGroupConstant.STORE) {
      getAllRestaurantResponsesItemsForExport(
        createDateFrom,
        createDateTo,
        requestId,
        requestBody,
      );
    } else {
      getAllFranchiseResponseItemsForExport(
        createDateFrom,
        createDateTo,
        requestId,
        requestBody,
      );
    }

    setExportHeaders(updatedColumnHeaders(headers, dayCounts));
  };

  const handleConfirmCustomerSurveyData = () => {
    handleAsyncAction(
      exportDetailCustomerSurveyData(
        timestampToDateTimeFormat(createDateFrom),
        timestampToDateTimeFormat(createDateTo),
        brandId,
      ),
      { onSuccess: closeModal },
    );
  };

  //Open confirmationModal for export customer survey data
  const openExportCustomerDataConfirmationModal = () => {
    setComponent(
      getConfirmationModal({
        ModalSize: "md",
        Title: "Confirmation",
        onClose: closeModal,
        modalBodyCssClassName: "text-center",
        selectedDateRange: `${UTCToDateFormat(
          createDateFrom,
        )} - ${UTCToDateFormat(createDateTo)}`,
        onConfirm: handleConfirmCustomerSurveyData,
        message:
          "Please confirm that you would like to export survey data for the following dates:",
        selectedDateRangeMessage:
          "Date ranges can be adjusted using the Selected Date parameters on the performance page",
      }),
    );
  };

  useEffect(() => {
    getData(0, null, restaurantResponseDataPagination.size);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchString,
    selectedResponse,
    selectedRatingOption,
    selectedOption,
    createDateTo,
    createDateFrom,
  ]);

  useEffect(() => {
    resetRestaurantProfile();
  }, [resetRestaurantProfile]);

  useEffect(() => {
    if (allItemsForExport.length) {
      const updatedData = allItemsForExport.map((item) => ({
        ...item,
        transactionChange: calculateChange(
          item.transactionRating,
          item.previousTransactionRating,
        ),
        sentimentChange: calculateChange(
          item.sentimentRating,
          item.previousSentimentRating,
        ),
      }));
      setExportData(updatedData);
    }
  }, [allItemsForExport]);

  useEffect(() => {
    if (exportData.length) {
      exportCSV();
      resetAllExportItems();
      setExportData([]);
    }
    // eslint-disable-next-line
  }, [exportData]);

  return (
    <>
      {showModal && modalElement}
      <FilterComponent
        dateState={dateState}
        setDateState={setDateState}
        selectedOption={selectedOption}
        setSelectedOption={setSelectedOption}
        selectedRatingOption={selectedRatingOption}
        handleRatingOptionChange={handleRatingOptionChange}
      />
      <PerformanceButtonGroup
        windowSize={windowSize}
        selectedResponse={selectedResponse}
        handleChangeResponse={handleChangeResponse}
      />
      {selectedResponse === buttonGroupConstant.STORE ? (
        <Performance
          getData={getData}
          sorting={sorting}
          dayCounts={dayCounts}
          tableTitle="Stores"
          sentimentTitle={AVERAGE_SENTIMENT_RATING}
          transactionTitle={AVERAGE_TRANSACTION_RATING}
          selectedOption={selectedOption}
          searchString={searchString}
          setSearchString={setSearchString}
          selectedResponse={selectedResponse}
          userResponsesData={restaurantResponsesData}
          totalRestaurants={allRestaurantResponsesData.totalItems}
          totalResponses={allRestaurantResponsesData.totalResponses}
          userResponseDataPagination={restaurantResponseDataPagination}
          starRatingGuestSentimentResponse={starRatingGuestSentimentResponse}
          exportFunc={() => getExportData(buttonGroupConstant.STORE)}
          avgSentimentAndTransactionRatingData={
            storesAvgSentimentAndTransactionRatingData
          }
          openExportCustomerDataConfirmationModal={
            openExportCustomerDataConfirmationModal
          }
        />
      ) : (
        <Performance
          isGroup
          getData={getData}
          sorting={sorting}
          dayCounts={dayCounts}
          tableTitle={"Groups"}
          sentimentTitle={AVERAGE_SENTIMENT_RATING}
          transactionTitle={AVERAGE_TRANSACTION_RATING}
          selectedOption={selectedOption}
          searchString={searchString}
          setSearchString={setSearchString}
          selectedResponse={selectedResponse}
          userResponsesData={franchiseResponsesData}
          totalRestaurants={allFranchiseResponsesData.totalItems}
          totalResponses={allFranchiseResponsesData.totalResponses}
          userResponseDataPagination={groupResponseDataPagination}
          starRatingGuestSentimentResponse={starRatingGuestSentimentResponse}
          exportFunc={() => getExportData(userRole)}
          avgSentimentAndTransactionRatingData={
            storesAvgSentimentAndTransactionRatingData
          }
          openExportCustomerDataConfirmationModal={
            openExportCustomerDataConfirmationModal
          }
        />
      )}

      <CSVLink
        target="_blank"
        ref={csvLinkRef}
        className="hidden"
        headers={exportHeaders}
        data={exportData}
        filename={`${fileName(
          exportFileName,
          createDateFrom,
          createDateTo,
        )}.csv`}
      />
    </>
  );
};

const mapStateToProps = (state) => ({
  reduxBrandId: state.storesReducer.brandId,
  brandPerformanceReducer: state.brandPerformanceReducer,
});

const mapDispatchToProps = (dispatch) => ({
  getAllRestaurantResponsesItemsForExport: (
    createDateFrom,
    createDateTo,
    brandId,
    body,
  ) =>
    dispatch(
      brandPerformanceActions.getAllRestaurantResponsesItemsForExport(
        createDateFrom,
        createDateTo,
        brandId,
        body,
      ),
    ),
  getAllFranchiseResponseItemsForExport: (
    createDateFrom,
    createDateTo,
    brandId,
    body,
  ) =>
    dispatch(
      brandPerformanceActions.getAllFranchiseResponseItemsForExport(
        createDateFrom,
        createDateTo,
        brandId,
        body,
      ),
    ),
  resetAllExportItems: () =>
    dispatch(brandPerformanceActions.resetAllExportItems()),
  resetRestaurantProfile: () =>
    dispatch(individualStoreProfileActions.resetRestaurantProfile()),
  exportDetailCustomerSurveyData: (createDateFrom, createDateTo, brandId) =>
    dispatch(
      brandPerformanceActions.exportDetailCustomerSurveyData(
        createDateFrom,
        createDateTo,
        brandId,
      ),
    ),
  getAverageRatingResponses: (payload) =>
    dispatch(brandPerformanceActions.getAverageRatingResponses(payload)),
  getRecordPaginationResponses: (payload) =>
    dispatch(brandPerformanceActions.getRecordPaginationResponses(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(BrandPerformance);
